import React, { FC, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { isEmpty } from 'lodash';

import { PaymentProcessEnum, PaymentStatusEnum, PredefinedMilisecondsTypeEnum } from '../../lib/enums';
import lang from '../../lib/language';
import { RootState } from '../../lib/store';
import { getDefaultSelectableTradingPLanFromState } from '../../lib/store/crm/helpers';
import { CRMCall } from '../../lib/store/crm/types';
import { getCurrentAccount } from '../../lib/store/selectors';
import { calculateTimeRemaining, formatTimeRemaining } from '../../lib/util/DateTimeHelpers';
import { isCallStatusReady } from '../../lib/util/error-handling/StatusByCallHelpers';

import IconClockLight from '../../assets/img/clock-light.svg';
import MedalAward from '../../assets/img/medal-award.svg';
import IconWarning from '../../assets/img/warning.png';
import { useIsMobile } from '../../hooks';
import { UI_ROUTES } from '../../util/routes';

import styles from './TournamentStartTimer.module.scss';

type Props = ReduxProps & {};

const TournamentStartTimer: FC<Props> = (props: Props): JSX.Element => {
  const isMobile = useIsMobile();
  const location = useLocation();
  const { pathname } = location;
  const { tradingPlan, isTradingPlanReady, transactions, currentAccount, isProfileNameCreated } = props;
  const { plans_accounts_activation: startDate } = tradingPlan;
  const [days, setDays] = useState(0);
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [isFirstAdjustmentCompleted, setIsFirstAdjustmentCompleted] = useState(false);
  const [hasNoRemainingTime, setHasNoRemainingTime] = useState(true); // we will display nothing until the calculation is complete that's why default is TRUE

  const emptyContainerPatterns = [
    UI_ROUTES.buy,
    UI_ROUTES.sell,
    UI_ROUTES.modify,
    UI_ROUTES.popularGroup,
    UI_ROUTES.fiftyTwoWeekHighLow,
    UI_ROUTES.epicMoversToday,
    UI_ROUTES.earningsCalendar,
    UI_ROUTES.dividendPayersAndHighGrowthCompaniesPath,
    UI_ROUTES.myAccountPersonalDetails,
  ];
  const welcomeContainerPatterns = [UI_ROUTES.overview, UI_ROUTES.discover, UI_ROUTES.myAccount, UI_ROUTES.myPosition];

  useEffect(() => {
    if (!isTradingPlanReady) {
      return;
    }
    calculateTimeRemaining(startDate, setHasNoRemainingTime, setDays, setHours, setMinutes, setSeconds); // Initial calculation
    const intervalId = setInterval(() => {
      calculateTimeRemaining(startDate, setHasNoRemainingTime, setDays, setHours, setMinutes, setSeconds);
    }, PredefinedMilisecondsTypeEnum.oneSecond);

    return () => clearInterval(intervalId); // eslint-disable-line consistent-return
  }, [startDate, isTradingPlanReady]);

  useEffect(() => {
    const adjustments = transactions.filter((transaction) =>
      transaction.payment_process === PaymentProcessEnum.Adjustment
      && transaction.account_reference_id === currentAccount);
    if (!isEmpty(adjustments)) {
      const oldestAdjustment = adjustments.reduce((oldest, current) =>
        (new Date(current.create_date) < new Date(oldest.create_date) ? current : oldest));
      setIsFirstAdjustmentCompleted(oldestAdjustment.payment_status === PaymentStatusEnum.Completed);
    }
  }, [transactions, currentAccount]);

  const showContainer = (patterns: string[]) =>
    patterns.some((pattern) =>
      pathname.includes(pattern));

  if (hasNoRemainingTime && !isFirstAdjustmentCompleted && isProfileNameCreated) {
    if (showContainer(emptyContainerPatterns)) {
      return <div className={styles.noNotification} />;
    }

    if (!showContainer(welcomeContainerPatterns)) {
      return <></>;
    }

    return (
      <div className={styles.notification}>
        <div className={styles.info}>
          <div className={styles.iconWrapper}>
            <img alt="" className={styles.icon} src={IconWarning} />
          </div>
          <div className={styles.text}>
            {lang.commonAccountSettingUp()}
          </div>
        </div>
      </div>
    );
  }

  if (showContainer(emptyContainerPatterns)) {
    return <div className={styles.noNotification} />;
  }

  if (hasNoRemainingTime) {
    return <div className={showContainer(welcomeContainerPatterns) ? styles.noNotification : undefined} />;
  }

  if (showContainer(welcomeContainerPatterns)) {
    return (
      <div className={styles.wrapper}>
        {!isMobile && (
          <div className={styles.left}>
            <img alt="" className={styles.image} src={MedalAward} />
          </div>
        )}
        <div className={styles.right}>
          <div className={styles.title}>{lang.commonWelcomeToTournament()}</div>
          <div className={styles.desc}>{lang.commonWelcomeToTournamentDescription()}</div>
          <div className={styles.desc}>
            {lang.commonWelcomeToTournamentStartingAt()}
            <img alt="" src={IconClockLight} className={styles.clock} />
            {formatTimeRemaining(days, hours, minutes, seconds)}
          </div>
        </div>
      </div>
    );
  }

  return <></>;
};

const mapStateToProps = (state: RootState) => ({
  tradingPlan: getDefaultSelectableTradingPLanFromState(state.crm),
  isTradingPlanReady: isCallStatusReady(CRMCall.searchTradingPlans, state.crm.statusByCall),
  isProfileNameCreated: !!(state.crm.individualExtendedInfo?.profile_name),
  transactions: state.payment.paymentsAll.data,
  currentAccount: getCurrentAccount(state),
});

const connector = connect(mapStateToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(TournamentStartTimer);
